import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiDownload, FiZoomIn, FiZoomOut, FiRotateCw } from 'react-icons/fi';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background-overlay);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    align-items: flex-start;
    padding-top: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.25rem;
    padding-top: 0.5rem;
  }
`;

const Container = styled(motion.div)`
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  max-width: 1200px;
  width: 85%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  @media (max-width: 1024px) {
    width: 90%;
    max-width: 90%;
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
    max-height: 95vh;
    border-radius: 0;
  }
  
  @media (max-width: 480px) {
    max-height: 98vh;
    border-radius: 0;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  background: var(--background-secondary);
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.75rem;
    flex-direction: column;
    align-items: stretch;
  }
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    flex: 1 1 100%;
    order: 1;
  }
  
  @media (max-width: 480px) {
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    gap: 0.375rem;
    order: 2;
    width: 100%;
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    gap: 0.25rem;
    flex-wrap: wrap;
    justify-content: space-between;
  }
`;

const ControlButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0;
  background: var(--background-primary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    width: 2.25rem;
    height: 2.25rem;
  }
  
  @media (max-width: 480px) {
    width: 2rem;
    height: 2rem;
    flex: 1;
    min-width: 2rem;
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  border-radius: 0;
  background: var(--error-color);
  color: var(--text-inverse);
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: var(--error-dark);
  }
  
  @media (max-width: 768px) {
    width: 2.25rem;
    height: 2.25rem;
  }
  
  @media (max-width: 480px) {
    width: 2rem;
    height: 2rem;
    flex: 1;
    min-width: 2rem;
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  background: var(--background-tertiary);
  overflow: auto;
  min-height: 0;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.25rem;
  }
`;

const PdfContainer = styled.div`
  position: relative;
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transform: ${props => `scale(${props.$scale}) rotate(${props.$rotation}deg)`};
  transform-origin: center;
  transition: transform 0.3s ease;
  width: 100%;
  max-width: 100%;
  height: calc(90vh - 160px);
  max-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 1024px) {
    height: calc(92vh - 160px);
  }
  
  @media (max-width: 768px) {
    border-radius: 0;
    height: calc(95vh - 150px);
  }
  
  @media (max-width: 480px) {
    border-radius: 0;
    height: calc(98vh - 140px);
  }
`;

const PdfEmbed = styled.iframe`
  display: block;
  width: 100%;
  max-width: 100%;
  height: 100%;
  min-height: 65vh;
  border: none;
  
  @media (max-width: 1024px) {
    min-height: 60vh;
  }
  
  @media (max-width: 768px) {
    min-height: 55vh;
  }
  
  @media (max-width: 480px) {
    min-height: 50vh;
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  text-align: center;
  color: var(--text-muted);
`;

const ErrorIcon = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 0;
  background: rgba(239, 68, 68, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--error-color);
  font-size: 1.5rem;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  color: var(--text-muted);
`;

const Spinner = styled.div`
  width: 2rem;
  height: 2rem;
  border: 3px solid rgba(216, 184, 137, 0.3);
  border-top: 3px solid var(--primary-color);
  border-radius: 0;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

function PdfViewer({ isOpen, onClose, pdfBase64, fileName = 'document.pdf' }) {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [useBlobUrl, setUseBlobUrl] = useState(false);

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleDownload = () => {
    if (pdfBase64) {
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${pdfBase64}`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePdfLoad = () => {
    setLoading(false);
    setError(false);
  };

  const handlePdfError = () => {
    setLoading(false);
    setError(true);
  };

  // Create appropriate URL based on browser compatibility
  useEffect(() => {
    if (pdfBase64 && isOpen && pdfBase64.trim() !== '') {
      // Clean the base64 string first
      let cleanBase64 = pdfBase64.trim();
      
      // Remove data URI prefix if present
      if (cleanBase64.includes(',')) {
        cleanBase64 = cleanBase64.split(',')[1];
      }
      
      // Remove any whitespace
      cleanBase64 = cleanBase64.replace(/\s/g, '');
      
      // Use blob URL for all browsers - it's more reliable
      try {
        // Validate base64 before processing
        const byteCharacters = atob(cleanBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        setUseBlobUrl(true);
        setLoading(true);
        setError(false);
      } catch (err) {
        console.error('Error creating PDF blob:', err);
        console.error('PDF base64 length:', cleanBase64.length);
        console.error('PDF base64 preview (first 200 chars):', cleanBase64.substring(0, 200));
        
        // Fallback: try using data URI directly
        try {
          const dataUri = `data:application/pdf;base64,${cleanBase64}`;
          setPdfUrl(dataUri);
          setUseBlobUrl(false);
          setLoading(true);
          setError(false);
        } catch (fallbackErr) {
          console.error('Fallback data URI also failed:', fallbackErr);
          setError(true);
          setLoading(false);
          setPdfUrl(null);
        }
      }
    } else if (isOpen && (!pdfBase64 || pdfBase64.trim() === '')) {
      // No valid PDF data
      setError(true);
      setLoading(false);
      setPdfUrl(null);
    }
  }, [pdfBase64, isOpen]);

  // Separate cleanup effect for blob URLs
  useEffect(() => {
    return () => {
      if (pdfUrl && useBlobUrl && pdfUrl.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(pdfUrl);
        } catch (err) {
          console.warn('Error revoking blob URL:', err);
        }
      }
    };
  }, [pdfUrl, useBlobUrl]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Container
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Header>
            <Title>{fileName}</Title>
            <Controls>
              <ControlButton onClick={handleZoomOut} disabled={scale <= 0.5}>
                <FiZoomOut size={16} />
              </ControlButton>
              <ControlButton onClick={handleZoomIn} disabled={scale >= 3}>
                <FiZoomIn size={16} />
              </ControlButton>
              <ControlButton onClick={handleRotate}>
                <FiRotateCw size={16} />
              </ControlButton>
              <ControlButton onClick={handleDownload}>
                <FiDownload size={16} />
              </ControlButton>
              <CloseButton onClick={onClose}>
                <FiX size={16} />
              </CloseButton>
            </Controls>
          </Header>
          
          <Content>
            {loading && (
              <LoadingSpinner>
                <Spinner />
                <div>Loading PDF...</div>
              </LoadingSpinner>
            )}
            
            {error && (
              <ErrorMessage>
                <ErrorIcon>
                  <FiX size={24} />
                </ErrorIcon>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                    Failed to load PDF
                  </div>
                  <div style={{ fontSize: '0.875rem' }}>
                    The PDF file could not be displayed. You can still download it.
                  </div>
                </div>
              </ErrorMessage>
            )}
            
            {pdfUrl && !error && pdfUrl.trim() !== '' && (
              <PdfContainer $scale={scale} $rotation={rotation}>
                <PdfEmbed
                  src={pdfUrl}
                  type="application/pdf"
                  onLoad={handlePdfLoad}
                  onError={handlePdfError}
                />
              </PdfContainer>
            )}
          </Content>
        </Container>
      </Overlay>
    </AnimatePresence>
  );
}

export default PdfViewer;
